import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/services/api';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Trash2, Rocket, Pencil, Save, X } from 'lucide-react';
import type { Blueprint } from '@/types';
import { apiFetch } from '../../lib/api';

export default function BlueprintDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Blueprint>>({});

  const { data: blueprint, isLoading } = useQuery({
    queryKey: ['blueprint', id],
    queryFn: () => apiService.getBlueprint(id!),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiService.deleteBlueprint(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blueprints'] });
      navigate('/blueprints');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<Blueprint>) =>
      fetch(`/mcp/blueprints/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      }).then(r => { if (!r.ok) throw new Error('Update failed'); return r.json(); }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blueprint', id] });
      setIsEditing(false);
    },
  });

  const startEditing = () => {
    if (!blueprint) return;
    setEditData({
      title: blueprint.title,
      description: blueprint.description,
      functionalRequirements: [...blueprint.functionalRequirements],
      nonFunctionalRequirements: [...(blueprint.nonFunctionalRequirements || [])],
      technicalStack: [...(blueprint.technicalStack || [])],
      tags: [...(blueprint.tags || [])],
      priority: blueprint.priority,
      estimatedComplexity: blueprint.estimatedComplexity,
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    updateMutation.mutate(editData);
  };

  const startDevMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch('/dev-engine/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blueprint }),
      });
      if (!res.ok) throw new Error('Failed to start development');
      return res.json();
    },
    onSuccess: (data) => {
      navigate(`/projects/${data.projectId}`);
    },
  });

  const handleStartDevelopment = () => {
    if (!blueprint) return;
    if (confirm('Start auto-development for this blueprint?')) {
      startDevMutation.mutate();
    }
  };

  if (isLoading) return <div className="container p-6 text-center">Loading...</div>;
  if (!blueprint) return <div className="container p-6 text-center">Blueprint not found</div>;

  return (
    <div className="container h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Link to="/blueprints">
            <Button variant="ghost"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
          </Link>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button variant="default" onClick={handleSave} disabled={updateMutation.isPending}>
                  <Save className="mr-2 h-4 w-4" /> {updateMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="ghost" onClick={() => setIsEditing(false)}>
                  <X className="mr-2 h-4 w-4" /> Cancel
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={startEditing}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </Button>
                <Button variant="default" onClick={handleStartDevelopment} disabled={startDevMutation.isPending}>
                  <Rocket className="mr-2 h-4 w-4" /> {startDevMutation.isPending ? 'Starting...' : 'Start Development'}
                </Button>
                <Button variant="destructive" onClick={() => {
                  if (confirm('Delete this blueprint?')) deleteMutation.mutate();
                }}>
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {/* Title */}
          {isEditing ? (
            <input
              className="w-full rounded border px-3 py-2 text-4xl font-bold focus:border-blue-500 focus:outline-none"
              value={editData.title || ''}
              onChange={e => setEditData(d => ({ ...d, title: e.target.value }))}
            />
          ) : (
            <h1 className="text-4xl font-bold">{blueprint.title}</h1>
          )}

          {/* Description */}
          {isEditing ? (
            <textarea
              className="w-full rounded border px-3 py-2 text-lg focus:border-blue-500 focus:outline-none"
              rows={3}
              value={editData.description || ''}
              onChange={e => setEditData(d => ({ ...d, description: e.target.value }))}
            />
          ) : (
            <p className="text-lg text-muted-foreground">{blueprint.description}</p>
          )}

          {/* Functional Requirements */}
          <div className="rounded-lg border bg-card p-6">
            <h2 className="mb-4 text-2xl font-semibold">Functional Requirements</h2>
            {isEditing ? (
              <div className="space-y-2">
                {(editData.functionalRequirements || []).map((req, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      className="flex-1 rounded border px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                      value={req}
                      onChange={e => {
                        const updated = [...(editData.functionalRequirements || [])];
                        updated[i] = e.target.value;
                        setEditData(d => ({ ...d, functionalRequirements: updated }));
                      }}
                    />
                    <button className="text-red-500 text-sm" onClick={() => {
                      const updated = (editData.functionalRequirements || []).filter((_, j) => j !== i);
                      setEditData(d => ({ ...d, functionalRequirements: updated }));
                    }}>Remove</button>
                  </div>
                ))}
                <button
                  className="text-sm text-blue-600 hover:underline"
                  onClick={() => setEditData(d => ({
                    ...d,
                    functionalRequirements: [...(d.functionalRequirements || []), ''],
                  }))}
                >+ Add requirement</button>
              </div>
            ) : (
              <ul className="list-inside list-disc space-y-2">
                {blueprint.functionalRequirements.map((req, i) => <li key={i}>{req}</li>)}
              </ul>
            )}
          </div>

          {/* Non-Functional Requirements */}
          <div className="rounded-lg border bg-card p-6">
            <h2 className="mb-4 text-2xl font-semibold">Non-Functional Requirements</h2>
            {isEditing ? (
              <div className="space-y-2">
                {(editData.nonFunctionalRequirements || []).map((req, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      className="flex-1 rounded border px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                      value={req}
                      onChange={e => {
                        const updated = [...(editData.nonFunctionalRequirements || [])];
                        updated[i] = e.target.value;
                        setEditData(d => ({ ...d, nonFunctionalRequirements: updated }));
                      }}
                    />
                    <button className="text-red-500 text-sm" onClick={() => {
                      const updated = (editData.nonFunctionalRequirements || []).filter((_, j) => j !== i);
                      setEditData(d => ({ ...d, nonFunctionalRequirements: updated }));
                    }}>Remove</button>
                  </div>
                ))}
                <button
                  className="text-sm text-blue-600 hover:underline"
                  onClick={() => setEditData(d => ({
                    ...d,
                    nonFunctionalRequirements: [...(d.nonFunctionalRequirements || []), ''],
                  }))}
                >+ Add requirement</button>
              </div>
            ) : (
              <ul className="list-inside list-disc space-y-2">
                {(blueprint.nonFunctionalRequirements || []).map((req, i) => <li key={i}>{req}</li>)}
              </ul>
            )}
          </div>

          {/* Technical Stack */}
          <div className="rounded-lg border bg-card p-6">
            <h2 className="mb-4 text-2xl font-semibold">Technical Stack</h2>
            {isEditing ? (
              <div className="space-y-2">
                <input
                  className="w-full rounded border px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                  value={(editData.technicalStack || []).join(', ')}
                  onChange={e => setEditData(d => ({
                    ...d,
                    technicalStack: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                  }))}
                  placeholder="Comma-separated: React, Node.js, MongoDB"
                />
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(blueprint.technicalStack || []).map((tech, i) => (
                  <span key={i} className="rounded-full bg-primary/10 px-3 py-1 text-sm text-primary">{tech}</span>
                ))}
              </div>
            )}
          </div>

          {/* Tags */}
          {(blueprint.tags?.length || isEditing) ? (
            <div className="rounded-lg border bg-card p-6">
              <h2 className="mb-4 text-2xl font-semibold">Tags</h2>
              {isEditing ? (
                <input
                  className="w-full rounded border px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                  value={(editData.tags || []).join(', ')}
                  onChange={e => setEditData(d => ({
                    ...d,
                    tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                  }))}
                  placeholder="Comma-separated tags"
                />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(blueprint.tags || []).map((tag, i) => (
                    <span key={i} className="rounded-full bg-secondary px-3 py-1 text-sm">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {/* Metadata */}
          <div className="rounded-lg border bg-card p-6">
            <h2 className="mb-4 text-2xl font-semibold">Metadata</h2>
            <dl className="grid grid-cols-2 gap-4">
              {isEditing ? (
                <>
                  <dt className="font-medium">Priority</dt>
                  <dd>
                    <select className="rounded border px-2 py-1 text-sm" value={editData.priority || ''} onChange={e => setEditData(d => ({ ...d, priority: e.target.value as any }))}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </dd>
                  <dt className="font-medium">Complexity</dt>
                  <dd>
                    <select className="rounded border px-2 py-1 text-sm" value={editData.estimatedComplexity || ''} onChange={e => setEditData(d => ({ ...d, estimatedComplexity: e.target.value as any }))}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </dd>
                </>
              ) : (
                <>
                  {blueprint.priority && (<><dt className="font-medium">Priority</dt><dd className="capitalize">{blueprint.priority}</dd></>)}
                  {blueprint.estimatedComplexity && (<><dt className="font-medium">Complexity</dt><dd className="capitalize">{blueprint.estimatedComplexity}</dd></>)}
                </>
              )}
              <dt className="font-medium">Created</dt>
              <dd>{new Date(blueprint.metadata.createdAt).toLocaleString()}</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
